/**
 * Cookie 配置选项
 */
interface CookieOptions {
  /** Cookie 过期时间（秒）或 Date 对象 */
  expires?: number | Date;
  /** Cookie 路径 */
  path?: string;
  /** Cookie 域名 */
  domain?: string;
  /** 是否仅 HTTPS */
  secure?: boolean;
  /** SameSite 属性 */
  sameSite?: 'Strict' | 'Lax' | 'None';
  /** 最大存活时间（秒） */
  maxAge?: number;
}

/**
 * Cookie 管理工具类
 * 提供统一的 Cookie 操作接口
 */
class CookieMgr {
  /**
   * 获取 Cookie
   * @param name - Cookie 名称
   * @returns Cookie 值或 null
   */
  static getCookie(name: string): string | null {
    if (typeof window !== 'undefined') {
      // 客户端获取 Cookie
      return this.getCookieFromString(document.cookie, name);
    }
    return null;
  }

  /**
   * 设置 Cookie
   * @param name - Cookie 名称
   * @param value - Cookie 值
   * @param options - Cookie 选项
   */
  static setCookie(name: string, value: string, options: CookieOptions = {}) {
    const cookieString = this.serializeCookie(name, value, options);
    if (typeof window !== 'undefined') {
      document.cookie = cookieString;
    }
  }

  /**
   * 删除 Cookie
   * @param name - Cookie 名称
   * @param options - Cookie 选项（需要与设置时的 path 和 domain 一致）
   */
  static removeCookie(name: string, options: CookieOptions = {}) {
    this.setCookie(name, '', {
      ...options,
      maxAge: -1,
    });
  }

  /**
   * 从 Cookie 字符串中获取指定 Cookie
   * @private
   */
  private static getCookieFromString(
    cookieString: string,
    name: string
  ): string | null {
    if (!cookieString) return null;
    const match = cookieString.match(
      new RegExp(`(^|; )${encodeURIComponent(name)}=([^;]+)`)
    );
    return match ? decodeURIComponent(match[2]) : null;
  }

  /**
   * 序列化 Cookie
   * @private
   */
  private static serializeCookie(
    name: string,
    value: string,
    options: CookieOptions
  ): string {
    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    // 处理过期时间
    if (options.expires) {
      const expires =
        options.expires instanceof Date
          ? options.expires
          : new Date(Date.now() + options.expires * 1000);
      cookie += `; Expires=${expires.toUTCString()}`;
    }

    // 处理 maxAge
    if (options.maxAge !== undefined) {
      cookie += `; Max-Age=${options.maxAge}`;
    }

    // 处理路径
    cookie += `; Path=${options.path || '/'}`;

    // 处理域名
    if (options.domain) {
      cookie += `; Domain=${options.domain}`;
    }

    // 处理 Secure
    if (options.secure) {
      cookie += '; Secure';
    }

    // 处理 SameSite
    if (options.sameSite) {
      cookie += `; SameSite=${options.sameSite}`;
    }

    return cookie;
  }
}

export default CookieMgr;
